import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Building2, Clock3, Mic, MicOff, PhoneIncoming, PhoneOff, UserRound } from 'lucide-react'
import { attachVoiceCall, CrmVoiceClient, getVoiceWorkspace, type VoiceCallAttempt, type VoiceDiagnostics, type VoiceState } from './lib/voice'
import { formatCallDuration, latestContactInteraction, resolveCaller, type CallWorkspaceDirectory, type VoiceHistoryCall, type VoiceSession } from './lib/callWorkspace'
import type { Contact } from './types'
import { useLocation } from 'react-router-dom'
import {createCrmPoller,setCrmCallActive} from './lib/crmSync'

type VoiceContextValue = {
  state: VoiceState
  failure: string
  diagnostics: VoiceDiagnostics | null
  incomingCalls: VoiceSession[]
  activeCall: VoiceSession | null
  lastEndedCall: VoiceSession | null
  recentCalls: VoiceHistoryCall[]
  actors: Record<string, string>
  resolveContact: (call: VoiceSession) => Contact | null
  configureWorkspace: (directory: CallWorkspaceDirectory, navigateToContact: (contactId: string) => void) => void
  refreshWorkspace: () => Promise<void>
  start: (contactId: string) => Promise<void>
  quickDial: (phone: string, prospectTaskId?: string) => Promise<VoiceCallAttempt>
  attachCall: (input:{attemptId:string;contactId?:string|null;organizationId?:string|null})=>Promise<void>
  retry: () => Promise<void>
  answer: (callId?: string) => Promise<void>
  reject: (callId?: string) => void
  viewProfile: (callId: string) => void
  hangup: () => void
  toggleMute: () => void
}

const VoiceContext = createContext<VoiceContextValue | null>(null)
const emptyDirectory: CallWorkspaceDirectory = { contacts: [], activities: [], dispositions: [] }
const failureCode = (value: unknown) => {
  const detail = typeof value === 'object' && value && 'code' in value ? String(value.code) : value instanceof Error ? value.message : 'connection_failed'
  return detail.replace(/[^a-z0-9_-]/gi, '').slice(0, 48) || 'connection_failed'
}
const secondsSince = (value: string | null) => value ? Math.max(0, Math.floor((Date.now() - Date.parse(value)) / 1000)) : 0

export function CrmVoiceProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation()
  const clientRef = useRef<CrmVoiceClient | null>(null)
  const directoryRef = useRef<CallWorkspaceDirectory>(emptyDirectory)
  const navigateRef = useRef<(contactId: string) => void>(() => undefined)
  const [state, setState] = useState<VoiceState>('ready')
  const [failure, setFailure] = useState('')
  const [diagnostics, setDiagnostics] = useState<VoiceDiagnostics | null>(null)
  const [sessions, setSessions] = useState<VoiceSession[]>([])
  const [recentCalls, setRecentCalls] = useState<VoiceHistoryCall[]>([])
  const [actors, setActors] = useState<Record<string, string>>({})
  const [, setClock] = useState(0)

  const historyPoller=useRef<ReturnType<typeof createCrmPoller<Awaited<ReturnType<typeof getVoiceWorkspace>>>>|null>(null)
  const historyActive=useRef(false)
  const refreshWorkspace = useCallback(()=>historyPoller.current?.refresh()??Promise.resolve(),[])
  useEffect(()=>{
    const poller=createCrmPoller({read:signal=>getVoiceWorkspace(signal),accept:workspace=>{setRecentCalls(workspace.calls);setActors(workspace.actors)},interval:()=>document.visibilityState==='hidden'?null:historyActive.current||window.location.pathname==='/communications/live'?15_000:60_000})
    historyPoller.current=poller;poller.start()
    return()=>{poller.dispose();if(historyPoller.current===poller)historyPoller.current=null}
  },[])
  useEffect(()=>historyPoller.current?.update(),[pathname])

  useEffect(() => {
    let active = true
    const client = new CrmVoiceClient(next => { if (active) setState(next) }, next => { if (active) setSessions(next) })
    const readDiagnostics = () => client.diagnostics()
    clientRef.current = client
    Object.defineProperty(window, '__avertsignalVoiceDiagnostics', { configurable: true, value: readDiagnostics })
    void client.register().then(() => {
      if (!active) return
      setFailure('')
      setDiagnostics(readDiagnostics())
    }).catch(value => {
      if (!active) return
      setFailure(failureCode(value))
      setState('failed')
      setDiagnostics(readDiagnostics())
    })
    const diagnosticsInterval = window.setInterval(() => { if (active) { const next = readDiagnostics(); setDiagnostics(next); if (next.lastRegistrationErrorCode) setFailure(String(next.lastRegistrationErrorCode)) } }, 500)
    return () => {
      active = false
      window.clearInterval(diagnosticsInterval)
      if (window.__avertsignalVoiceDiagnostics === readDiagnostics) delete window.__avertsignalVoiceDiagnostics
      if (clientRef.current === client) clientRef.current = null
      client.destroy()
    }
  }, [refreshWorkspace])

  const incomingCalls = useMemo(() => sessions.filter(call => call.state === 'ringing' && call.direction === 'INBOUND'), [sessions])
  const activeCall = useMemo(() => sessions.find(call => call.state === 'connected' || (call.direction === 'OUTBOUND' && call.state === 'ringing')) || null, [sessions])
  const callInProgress=['connecting','dialing','ringing','connected'].includes(state)||Boolean(activeCall)||incomingCalls.length>0
  useEffect(()=>{historyActive.current=callInProgress;setCrmCallActive(callInProgress);historyPoller.current?.update();return()=>setCrmCallActive(false)},[callInProgress])
  const lastEndedCall = useMemo(() => sessions.filter(call => call.state === 'ended' && call.connectedAt).sort((a, b) => Date.parse(b.endedAt || '') - Date.parse(a.endedAt || ''))[0] || null, [sessions])
  const resolveContact = useCallback((call: VoiceSession) => {
    if (call.direction === 'OUTBOUND' && call.contactId) return directoryRef.current.contacts.find(contact => contact.id === call.contactId) || null
    return resolveCaller(call.phone, call.contactId, directoryRef.current.contacts)
  }, [])

  useEffect(() => {
    if (!activeCall) return
    const timer = window.setInterval(() => setClock(value => value + 1), 1000)
    return () => window.clearInterval(timer)
  }, [activeCall])
  useEffect(() => {
    if (!lastEndedCall) return
    const timer = window.setTimeout(() => void refreshWorkspace(), 1500)
    return () => window.clearTimeout(timer)
  }, [lastEndedCall, refreshWorkspace])

  const configureWorkspace = useCallback((directory: CallWorkspaceDirectory, navigateToContact: (contactId: string) => void) => {
    directoryRef.current = directory
    navigateRef.current = navigateToContact
  }, [])
  const answer = useCallback(async (callId?: string) => {
    const call = sessions.find(item => item.id === callId) || incomingCalls[0]
    if (!call) throw new Error('voice_call_unavailable')
    const contact = resolveContact(call)
    await clientRef.current?.answer(call.id)
    if (contact) navigateRef.current(contact.id)
  }, [incomingCalls, resolveContact, sessions])
  const reject = useCallback((callId?: string) => clientRef.current?.reject(callId), [])
  const viewProfile = useCallback((callId: string) => {
    const call = sessions.find(item => item.id === callId)
    const contact = call ? resolveContact(call) : null
    if (contact) navigateRef.current(contact.id)
  }, [resolveContact, sessions])

  const value = useMemo<VoiceContextValue>(() => ({
    state, failure, diagnostics, incomingCalls, activeCall, lastEndedCall, recentCalls, actors, resolveContact, configureWorkspace, refreshWorkspace,
    start: async contactId => { setFailure(''); try { const client=clientRef.current;if(!client)throw new Error('voice_device_unavailable');await client.connect(contactId) } catch (error) { if(failureCode(error)!=='voice_call_in_progress'){setFailure(failureCode(error));setState('failed')} throw error } },
    quickDial: async (phone,prospectTaskId)=>{setFailure('');try{const client=clientRef.current;if(!client)throw new Error('voice_device_unavailable');return await client.quickDial(phone,prospectTaskId)}catch(error){if(failureCode(error)!=='voice_call_in_progress'){setFailure(failureCode(error));setState('failed')}throw error}},
    attachCall: async input=>{await attachVoiceCall(input);await refreshWorkspace()},
    retry: async () => { setFailure(''); try { const client=clientRef.current;if(!client)throw new Error('voice_device_unavailable');await client.register() } catch (error) { setFailure(failureCode(error)); setState('failed'); throw error } },
    answer, reject, viewProfile,
    hangup: () => clientRef.current?.hangup(),
    toggleMute: () => clientRef.current?.toggleMute(),
  }), [activeCall, actors, answer, configureWorkspace, diagnostics, failure, incomingCalls, lastEndedCall, recentCalls, refreshWorkspace, reject, resolveContact, state, viewProfile])

  return <VoiceContext.Provider value={value}>
    <output hidden data-voice-device-diagnostics data-device-state={diagnostics?.deviceState || 'pending'} data-identity={diagnostics?.identity || ''} data-token-expired={diagnostics?.tokenExpired == null ? 'unknown' : String(diagnostics.tokenExpired)} data-incoming-listener-attached={String(diagnostics?.incomingListenerAttached === true)} data-signaling-active={String(diagnostics?.signalingActive === true)} data-disposed={String(diagnostics?.disposed === true)} />
    {children}
    {incomingCalls.length && !activeCall && pathname !== '/communications/live' ? <aside className="global-incoming-voice" aria-label="Incoming Voice calls" aria-live="assertive">{incomingCalls.map(call => {
      const contact = resolveContact(call)
      const prior = contact ? latestContactInteraction(contact.id, recentCalls, directoryRef.current.activities, directoryRef.current.dispositions) : null
      const priorActor = prior?.actorUserId ? actors[prior.actorUserId] || 'Unknown CRM user' : ''
      const owner = contact?.ownerUserId ? actors[contact.ownerUserId] || contact.owner : 'Unassigned'
      return <article className="incoming-call-banner" key={call.id} role="alertdialog" aria-label={`Incoming Voice call from ${contact?.name || call.phone || 'unknown caller'}`}>
        <span className="incoming-priority"><PhoneIncoming size={18} /></span>
        <div className="incoming-caller-identity"><span>INCOMING · {formatCallDuration(secondsSince(call.startedAt))}</span><strong>{contact?.name || 'Unknown caller'}</strong><small>{contact?.company || call.phone || 'Phone unavailable'}{contact ? ` · ${call.phone}` : ''}</small></div>
        <div className="incoming-account-signal"><b>{contact?.company || 'Unmatched call'}</b><span>Assigned: {owner}</span><small>{prior ? `Last spoke with ${priorActor} · ${new Date(prior.occurredAt).toLocaleString()} · ${prior.outcome}` : 'No previous contact'}</small></div>
        {contact && <button className="secondary-btn" type="button" onClick={() => viewProfile(call.id)}><UserRound size={14} /> View profile</button>}
        <button className="answer-call-btn" type="button" disabled={Boolean(activeCall)} aria-label={`Answer call from ${contact?.name || call.phone || 'unknown caller'}`} onClick={() => void answer(call.id)}>Answer</button>
        <button className="secondary-btn" type="button" aria-label={`Decline call from ${contact?.name || call.phone || 'unknown caller'}`} onClick={() => reject(call.id)}>Decline</button>
      </article>
    })}</aside> : null}
    {activeCall ? <ActiveVoiceBar call={activeCall} contact={resolveContact(activeCall)} onProfile={contactId => navigateRef.current(contactId)} onMute={value.toggleMute} onEnd={value.hangup} /> : null}
  </VoiceContext.Provider>
}

function ActiveVoiceBar({ call, contact, onProfile, onMute, onEnd }: { call: VoiceSession; contact: Contact | null; onProfile: (contactId: string) => void; onMute: () => void; onEnd: () => void }) {
  const connected = call.state === 'connected'
  const elapsed = call.connectedAt ? secondsSince(call.connectedAt) : secondsSince(call.startedAt)
  const statusLabel = connected ? 'CONNECTED' : 'CALLING'
  return <aside className="global-active-voice" role="region" aria-label="Active Voice call controls">
    <div className="active-voice-identity"><span><i className="csr-live-dot" /> {statusLabel}</span><strong>{contact?.name || 'Unknown caller'}</strong><small><Building2 size={12} /> {contact?.company || call.phone || 'Unmatched call'}</small></div>
    <div className="active-voice-time"><Clock3 size={14} /><strong>{formatCallDuration(elapsed)}</strong></div>
    {contact && <button type="button" className="secondary-btn" onClick={() => onProfile(contact.id)}>View profile</button>}
    {connected ? <button type="button" className={call.muted ? 'secondary-btn active' : 'secondary-btn'} aria-label={call.muted ? 'Unmute active call' : 'Mute active call'} onClick={onMute}>{call.muted ? <MicOff size={15} /> : <Mic size={15} />}{call.muted ? 'Unmute' : 'Mute'}</button> : null}
    <button type="button" className="end-call-btn" aria-label={connected ? 'End active call' : 'Cancel outbound call'} onClick={onEnd}><PhoneOff size={15} /> {connected ? 'End' : 'Cancel'}</button>
  </aside>
}

export function useCrmVoice() {
  const voice = useContext(VoiceContext)
  if (!voice) throw new Error('crm_voice_provider_missing')
  return voice
}

declare global { interface Window { __avertsignalVoiceDiagnostics?: () => VoiceDiagnostics } }
